/**
 * Hộp "Nhập danh mục hành chính từ tệp" của MH-36 — hai bước như ba trợ lý khác:
 * chọn tệp → XEM TRƯỚC (thêm mới / đã có / khác tên / lỗi) → XÁC NHẬN nhập.
 *
 * Đọc CSV ngay trên trình duyệt (dùng lại `docCsv` của MH-06) và nhận hai dạng cột:
 *   - bảng mã quốc gia: MDD_01, TEN_01, MDD_02, TEN_02, CAP_TEN_02, MDD_03… — mỗi
 *     dòng sinh ra một đơn vị cho MỖI cấp có mã (tỉnh, xã, thôn), bỏ trùng;
 *   - dạng đơn giản: ma_dinh_danh, ten, cap, ma_cha.
 */
import { useId, useState, type ReactNode } from 'react';
import { dienVao, vi } from '../ngon-ngu/vi';
import { danhMucApi, LoiApi, type DongNhapDonVi, type KetQuaNhapDonVi } from '../api/danh-muc-hanh-chinh-api';
import { docCsv, oCsv } from './MH06NhapKhau';
import './nha-cung-cap.css';

const t = vi.danhMucHanhChinh;

/** Tên cột → khoá so khớp: chữ thường, bỏ dấu, bỏ ký tự không phải chữ/số. */
const khoaCot = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

export interface DongDaDoc extends DongNhapDonVi {
  /** Số dòng trong tệp (dòng tiêu đề là 1) để báo lỗi đúng chỗ. */
  dongTep: number;
}

/** Đọc văn bản CSV thành danh sách đơn vị; null khi không nhận ra cột. */
export function docTepDonVi(van: string): DongDaDoc[] | null {
  // Bỏ dấu BOM (U+FEFF) ở đầu tệp CSV do Excel ghi ra.
  const { tieuDe, dong } = docCsv(van.charCodeAt(0) === 0xfeff ? van.slice(1) : van);
  const k = tieuDe.map(khoaCot);
  const cot = (...ten: string[]): number => k.findIndex((x) => ten.includes(x));
  const kq: DongDaDoc[] = [];
  const daCo = new Set<string>();
  const them = (d: DongDaDoc): void => {
    const ma = d.maDinhDanh.trim().toUpperCase();
    if (!ma || daCo.has(ma)) return;
    daCo.add(ma);
    kq.push({ ...d, maDinhDanh: ma });
  };

  // Dạng bảng mã quốc gia: các cặp MDD_0n / TEN_0n.
  const cap = [1, 2, 3]
    .map((n) => ({ ma: cot(`mdd0${n}`), ten: cot(`ten0${n}`), capTen: cot(`capten0${n}`) }))
    .filter((c) => c.ma >= 0 && c.ten >= 0);
  if (cap.length > 0) {
    dong.forEach((o, i) => {
      for (const c of cap) {
        them({
          maDinhDanh: o[c.ma] ?? '',
          ten: o[c.ten] ?? '',
          cap: c.capTen >= 0 ? o[c.capTen] : undefined,
          dongTep: i + 2,
        });
      }
    });
    return kq;
  }

  // Dạng đơn giản.
  const cMa = cot('madinhdanh', 'ma', 'madonvi', 'mdd');
  const cTen = cot('ten', 'tendonvi');
  if (cMa < 0 || cTen < 0) return null;
  const cCap = cot('cap', 'capten');
  const cCha = cot('macha', 'madonvicha');
  dong.forEach((o, i) =>
    them({
      maDinhDanh: o[cMa] ?? '',
      ten: o[cTen] ?? '',
      cap: cCap >= 0 ? o[cCap] : undefined,
      maCha: cCha >= 0 ? o[cCha] || undefined : undefined,
      dongTep: i + 2,
    }),
  );
  return kq;
}

/** Tệp mẫu dạng đơn giản, có BOM để Excel mở đúng tiếng Việt. */
function taiTepMau(): void {
  const dong = [
    ['ma_dinh_danh', 'ten', 'cap', 'ma_cha'],
    ['H44', 'Tỉnh Phú Thọ', 'tỉnh', ''],
    ['H44.167', 'xã Sông Lô', 'xã', 'H44'],
    ['H44.167.001', 'Thôn Một', 'thôn', 'H44.167'],
  ];
  const blob = new Blob([String.fromCharCode(0xfeff) + dong.map((d) => d.map(oCsv).join(',')).join('\r\n')], {
    type: 'text/csv;charset=utf-8',
  });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'mau-danh-muc-hanh-chinh.csv';
  a.click();
  URL.revokeObjectURL(a.href);
}

export function HopNhapDonVi({
  api = danhMucApi,
  onDong,
  onDaNhap,
}: {
  api?: Pick<typeof danhMucApi, 'nhap'>;
  onDong: () => void;
  onDaNhap: (so: number) => void;
}): ReactNode {
  const idTieuDe = useId();
  const [dong, datDong] = useState<DongDaDoc[] | null>(null);
  const [xemTruoc, datXemTruoc] = useState<KetQuaNhapDonVi | null>(null);
  const [dangGui, datDangGui] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);

  const chonTep = async (tep: File | undefined): Promise<void> => {
    datDong(null);
    datXemTruoc(null);
    datLoi(null);
    if (!tep) return;
    const ds = docTepDonVi(await tep.text());
    if (!ds) return datLoi(t.khongNhanRaCot);
    if (ds.length === 0) return datLoi(t.tepRong);
    datDong(ds);
    datDangGui(true);
    try {
      datXemTruoc(await api.nhap({ dong: ds.map(({ dongTep: _b, ...d }) => d) }));
    } catch (e) {
      datLoi(e instanceof LoiApi || e instanceof Error ? e.message : String(e));
    } finally {
      datDangGui(false);
    }
  };

  const nhap = async (): Promise<void> => {
    if (!dong) return;
    datDangGui(true);
    datLoi(null);
    try {
      const r = await api.nhap({ dong: dong.map(({ dongTep: _b, ...d }) => d), xacNhan: true });
      onDaNhap(r.soThemMoi);
    } catch (e) {
      datLoi(e instanceof LoiApi || e instanceof Error ? e.message : String(e));
    } finally {
      datDangGui(false);
    }
  };

  return (
    <div className="man-che" role="presentation">
      <div className="hop-thoai ncc-bieu-mau" role="dialog" aria-modal="true" aria-labelledby={idTieuDe}>
        <h2 id={idTieuDe}>{t.nhapTieuDe}</h2>
        <p>{t.nhapMoTa}</p>
        <p>
          <button type="button" className="nut nut--nho" onClick={taiTepMau}>
            {t.taiTepMau}
          </button>
        </p>
        <label>
          {t.chonTep}
          <input type="file" accept=".csv,text/csv" onChange={(e) => void chonTep(e.target.files?.[0])} />
        </label>
        {dangGui && !xemTruoc ? <p role="status">{t.dangDoc}</p> : null}
        {loi ? (
          <p className="ncc-loi" role="alert">
            {loi}
          </p>
        ) : null}
        {xemTruoc ? (
          <section data-vung="xem-truoc-nhap" aria-label={t.ketQuaXemTruoc}>
            <h3 className="ncc-tieu-de-nho">{t.ketQuaXemTruoc}</h3>
            <dl className="ncc-thong-ke">
              <dt>{t.soDongTep}</dt>
              <dd>{xemTruoc.tongDong}</dd>
              <dt>{t.soThemMoi}</dt>
              <dd>
                <strong>{xemTruoc.soThemMoi}</strong>
              </dd>
              <dt>{t.soDaCo}</dt>
              <dd>{xemTruoc.soDaCo}</dd>
              <dt>{t.soKhacTen}</dt>
              <dd>{xemTruoc.khacTen.length}</dd>
              <dt>{t.soLoi}</dt>
              <dd>{xemTruoc.soLoi}</dd>
            </dl>
            {xemTruoc.tuThemTinh ? (
              <p>{dienVao(t.tuThemTinh, { ma: xemTruoc.mauThemMoi[0]?.maDinhDanh ?? '' })}</p>
            ) : null}
            {xemTruoc.loi.length > 0 ? (
              <ul className="ncc-ds-loi">
                {xemTruoc.loi.slice(0, 20).map((l) => (
                  <li key={`${l.chiSo}-${l.maDinhDanh}`}>
                    {dienVao(t.dongLoi, {
                      dong: dong?.[l.chiSo]?.dongTep ?? l.chiSo + 2,
                      ma: l.maDinhDanh || '—',
                      loi: l.loi,
                    })}
                  </li>
                ))}
              </ul>
            ) : null}
            {xemTruoc.khacTen.length > 0 ? (
              <ul className="ncc-ds-loi">
                {xemTruoc.khacTen.slice(0, 10).map((k) => (
                  <li key={k.maDinhDanh}>
                    {k.maDinhDanh}: “{k.tenHienTai}” → “{k.tenTrongTep}”
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        ) : null}
        <div className="man-hinh__hanh-dong">
          <button type="button" className="nut" onClick={onDong}>
            {t.huy}
          </button>
          <button
            type="button"
            className="nut nut--chinh"
            data-hanh-dong="xac-nhan-nhap"
            disabled={!xemTruoc || xemTruoc.soThemMoi === 0 || dangGui}
            title={xemTruoc && xemTruoc.soThemMoi === 0 ? t.khongCoGiThem : undefined}
            onClick={() => void nhap()}
          >
            {dienVao(t.nhapXacNhan, { so: xemTruoc?.soThemMoi ?? 0 })}
          </button>
        </div>
      </div>
    </div>
  );
}
